import React, { useLayoutEffect } from 'react';
import { compose, path, pick, pipe, prop } from 'ramda';
import { pure } from 'recompose';
import { useUserSelector, useAppSelector } from 'js/store/hooks';
import { withTranslations } from 'js/components/hocs/withTranslations';
import { Image } from 'js/components/common/Image';
import { Icon } from 'js/components/common/Icon';
import { FavoriteIcon } from 'js/components/common/Game/FavoriteIcon';
import FeatureIcons from 'js/components/common/Game/FeatureIcons';
import withGameLaunch from 'js/components/common/Game/withGameLaunch';
import { DemoButton, PlayButton } from 'js/components/common/Game/Buttons';
import { usePortletConfig } from 'js/components/system/Portlet';
import { createMarkup } from '../createMarkup';
import { useGameImageFallback } from '@theme/js/hooks/useGameImageFallback';
import style from './index.scss';

export const Description = withTranslations(({ code, t }) => {
    const tKey = `playtech.mobile.system.gamedescription.${code}`;
    const MAX_LENGTH = 145;
    let value = t(tKey);

    value = value.includes(tKey)
        ? ''
        : value.length > MAX_LENGTH ? `${value.substring(0, MAX_LENGTH)}...` : value;

    const missionValue = value && value.match(/\[(.*?)\]/); // get value from brackets []

    /* If value in brackets exist we use it, if not then we use just value. */
    return value && <p className={style.desc}
                       dangerouslySetInnerHTML={createMarkup(missionValue == null ? value : missionValue[1])} />;
});

export const HoveredGame = ({
    code,
    setGameInfoRef,
    showFavoriteIcon,
    flipToBack,
    flipToFront,
    setFlipRef,
    isRecentlyPlayed,
    isFavorites
}) => {
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const { images, name, funSupport } = useAppSelector(pipe(
        path(['games', code]),
        pick(['images', 'name', 'funSupport'])
    ));
    const { disableDemoWhenLoggedIn } = usePortletConfig();
    const showDemoButton = !(
        funSupport === 'unavailable'
        || isLoggedIn && (funSupport === 'guest' || disableDemoWhenLoggedIn)
    ) && !(isFavorites || isRecentlyPlayed);

    const {
        landscapeImageSrc: imageURL,
        handleSetLandscapeImage
    } = useGameImageFallback({
        image: !isLoggedIn && images.guest || images.base,
        gameCode: code
    });

    useLayoutEffect(() => {
        handleSetLandscapeImage(!isLoggedIn && images.guest || images.base);
    }, [handleSetLandscapeImage, images.base, images.guest, isLoggedIn]);

    return (
        <div ref={setGameInfoRef} className={style.gameInfoContainer}>
            <div ref={setFlipRef} className={style.flip}>
                <div className={style.front}>
                    <Image
                        className={style.image}
                        src={imageURL}
                    />
                    <div className={style.gameBackground} />
                    <div className={style.gameContent}>
                        <FeatureIcons code={code}
                            types={['gameStudio']}
                            classNames={{ item: style.gameStudio }} />
                        <FeatureIcons code={code}
                            types={['volatility']}
                            classNames={{ item: style.volatility }}
                            onClick={flipToBack} />
                        <h3 className={style.title}>{name}</h3>
                        <div className={style.buttons}>
                            {showDemoButton && <DemoButton code={code} name={name} isHoverBtn={true} />}
                            <PlayButton code={code} name={name} isHoverBtn={true} />
                        </div>
                        <Description code={code} />
                        <div className={style.bottomIcons}>
                            <FeatureIcons code={code}
                                className={style.featuredIcons}
                                types={['fsb', 'goldenChips', 'wilds']}
                                classNames={{ item: style.featured }} />
                            {showFavoriteIcon && <FavoriteIcon
                                code={code}
                                className={style.favorite}
                            />}
                        </div>
                    </div>
                </div>
                <div className={`${style.back} ${style.volatilityContent}`}>
                    <Icon type="close_svg"
                        onClick={flipToFront}
                        className={style.close} />
                    <FeatureIcons code={code}
                        types={['volatility']}
                        className={style.volatilityContainer}
                        classNames={{ image: style.volatility }}
                        showTitle={true} />
                </div>
            </div>
        </div>
    );
};

export default compose(
    pure,
    withGameLaunch
)(HoveredGame);
